// Phase 5a analytics SDK call wrappers + react-query hooks.

import { useQuery } from '@tanstack/react-query';
import {
  analyticsControllerTopline,
  analyticsControllerDaily,
  analyticsControllerHourly,
  analyticsControllerPlatforms,
  analyticsControllerStores,
  analyticsControllerMenus,
  analyticsControllerToppings,
  analyticsControllerAnomalies,
  analyticsControllerKeywords,
  analyticsControllerInventoryUsage,
  analyticsControllerRecipeMiss,
} from '@dvvb/sdk';
import { unwrap, unwrapItems } from '../../lib/envelope';
import type {
  AnalyticsToplineResponse,
  AnalyticsDailyPoint,
  AnalyticsHourlyPoint,
  AnalyticsPlatformRow,
  AnalyticsStoreRow,
  AnalyticsMenuRow,
  AnalyticsToppingRow,
  AnalyticsAnomalyRow,
  AnalyticsKeywordRow,
  AnalyticsInventoryUsageRow,
} from '../../types/api';
import type { Period, Compare } from './controls';

export interface AnalyticsScope {
  period: Period;
  compare: Compare;
  storeId?: string | null;
  platformId?: string | null;
}

function scopeQuery(scope: AnalyticsScope) {
  const q: Record<string, string> = {
    period: scope.period,
    compare: scope.compare,
  };
  if (scope.storeId) q.storeId = scope.storeId;
  if (scope.platformId) q.platformId = scope.platformId;
  return q;
}

function scopeKey(scope: AnalyticsScope) {
  return [scope.period, scope.compare, scope.storeId ?? null, scope.platformId ?? null];
}

export function useTopline(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'topline', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerTopline({ query: scopeQuery(scope) as never });
      return unwrap<AnalyticsToplineResponse>(r);
    },
  });
}

export function useDaily(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'daily', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerDaily({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsDailyPoint>(r);
    },
  });
}

export function useHourly(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'hourly', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerHourly({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsHourlyPoint>(r);
    },
  });
}

export function usePlatforms(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'platforms', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerPlatforms({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsPlatformRow>(r);
    },
  });
}

export function useStores(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'stores', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerStores({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsStoreRow>(r);
    },
  });
}

export function useMenus(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'menus', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerMenus({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsMenuRow>(r);
    },
  });
}

// Phase 5b-2 — operational placeholders → real endpoints.

export function useToppings(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'toppings', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerToppings({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsToppingRow>(r);
    },
  });
}

export function useAnomalies(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'anomalies', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerAnomalies({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsAnomalyRow>(r);
    },
  });
}

export function useKeywords(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'keywords', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerKeywords({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsKeywordRow>(r);
    },
  });
}

export function useInventoryUsage(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'inventoryUsage', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerInventoryUsage({ query: scopeQuery(scope) as never });
      return unwrapItems<AnalyticsInventoryUsageRow>(r);
    },
  });
}

// 2026-05-01 [4] — recipe_miss 배너 (메뉴는 팔렸으나 recipe 0건 alert).
// 응답: { period, count, samples: [{ saleEventId, occurredAt, storeId, storeName, totalAmount }] }
export interface AnalyticsRecipeMissResponse {
  period: { from: string; to: string; days: number };
  count: number;
  samples: Array<{
    saleEventId: string;
    occurredAt: string;
    storeId: string;
    storeName: string | null;
    totalAmount: number;
  }>;
}

export function useRecipeMiss(scope: AnalyticsScope) {
  return useQuery({
    queryKey: ['analytics', 'recipeMiss', ...scopeKey(scope)],
    queryFn: async () => {
      const r = await analyticsControllerRecipeMiss({ query: scopeQuery(scope) as never });
      return unwrap<AnalyticsRecipeMissResponse>(r);
    },
    // 30초 stale — 자연 발생 sale_event 변화에 빠르게 반영.
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
}
